import { debounce } from "lodash"
import { ListControllerProps, Record as RaRecord, useListController } from "ra-core"
import { ListProps } from "ra-core/esm/controller/useListController"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

const useLocalListController = <RecordType extends RaRecord = RaRecord>(
  props: ListProps,
  localListOptions?: {
    sorters?: Record<string, (a:RecordType, b:RecordType) => number>,
  },
): ListControllerProps<RecordType> => {
  const ctrls = useListController<RecordType>(props)
  const { 
    data, 
    page: innerPage, 
    perPage: innerPerPage,
  } = ctrls

  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(innerPerPage)
  const [sort, setSort] = useState<{field:string, order?:string}>({field:'id'})
  const setSortFn = useCallback((sort:string, order?:string) => {
    setSort({field: sort, order})
  }, [])

  const [innerVer, setInnerVer] = useState(new Date())
  const filterInfoRef = useRef({ filter: {} as any, displayedFilters: [] as string[]})

  const showFilter = useCallback((filterName:string) => {
    if (Object.keys(filterInfoRef.current.filter).indexOf(filterName) >= 0
    && filterInfoRef.current.displayedFilters.indexOf(filterName) < 0) {
      filterInfoRef.current = { ...filterInfoRef.current, displayedFilters: [...filterInfoRef.current.displayedFilters, filterName] }
      setInnerVer(new Date())
    }
  }, [])

  const hideFilter = useCallback((filterName:string) => {
    const i = filterInfoRef.current.displayedFilters.indexOf(filterName)
    if (i >= 0) {
      const displayedFilters = [...filterInfoRef.current.displayedFilters]
      displayedFilters.splice(i, 1)
      filterInfoRef.current = { ...filterInfoRef.current, displayedFilters }
      setInnerVer(new Date())
    }
  }, [])

  const dataArr = Object.values(data)
  const filteredDataArr =
    dataArr.length === 0 || Object.keys(filterInfoRef.current.filter).length === 0
    ? dataArr
    : Object.keys(filterInfoRef.current.filter).reduce((acc, fname) => {
      const fvalue = filterInfoRef.current.filter[fname]
      if (fvalue === undefined || fvalue === null) {
        return acc;
      } else if (typeof fvalue === 'function') {
        return acc.filter(fvalue)
      } else if (typeof fvalue === 'string') {
        return acc.filter((x) => { 
          try
          {
            return x[fname].indexOf(fvalue) >= 0 
          }
          catch { return false }
        })
      } else {
        return acc.filter(x => x[fname] === fvalue)
      }
    }, dataArr)

  const total = filteredDataArr.length
  
  const sorters = localListOptions?.sorters || {}
  const ids = 
    filteredDataArr.sort((a, b) => {
      const sorter = sorters[sort.field]
      if (!sorter) return -1

      const i = sorter(a, b)
      return sort.order === 'DESC' ? -i : i
    })
    .map(x => x.id)
    .splice((page - 1) * perPage, perPage)

  const pageData = ids.reduce((acc, id) => {
    acc[id] = data[id]
    return acc
  }, {} as { [key: string]: RecordType })

  const safeSetPage = useCallback((x:number) => {
    if (perPage < 1 || total < 0) return
    const maxPage = total === 0 ? 1 : Math.ceil(total / perPage)
    const target = Math.min(Math.max(1, x) , maxPage)
    setPage(target)
  }, [perPage, total])

  const safeSetPerPage = useCallback((x:number) => {
    const target = Math.max(1, x)
    setPerPage(target)
    setPage(1)
  }, [])

  const debouncedSetFilters = useMemo(() => {
    return debounce((filter: any) => {
      filterInfoRef.current = { ...filterInfoRef.current, filter }
      setInnerVer(new Date())
      setPage(1)
    }, 500)
  }, [setPage])

  const setFilters = useCallback((filter: any, displayedFilters?: any, debounce?: boolean) => {
    if (debounce) {
      debouncedSetFilters(filter)
    } else {
      filterInfoRef.current = { ...filterInfoRef.current, filter }
      setInnerVer(new Date())
      setPage(1)
    }
  }, [setPage, debouncedSetFilters])

  useEffect(() => {
    if (ids.length === 0 && page > 1) {
      setPage(1)
    }
  }, [page, ids.length])

  return {
    ...ctrls,
    page,
    setPage: safeSetPage,
    perPage,
    setPerPage: safeSetPerPage,
    currentSort: { field: sort.field, order: sort.order || 'ASC' },
    setSort: setSortFn,
    setFilters,
    showFilter,
    hideFilter,
    displayedFilters: filterInfoRef.current.displayedFilters,
    filterValues: filterInfoRef.current.filter,
    data: pageData,
    ids,
    total,
  }
}

export default useLocalListController;
